import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { AlertCircle, CheckCircle } from "@untitledui/icons";
import { Select } from "@tricura/atlas-ui";
import { fromWireSubmission, getSubmission, patchSubmission } from "../../api";
import { FeaturedAlert } from "../../components/featured-alert";
import { OrionSidebar } from "../../components/orion-sidebar";
import { SubmissionDetailSkeleton, SubmissionHeaderSkeleton, SubmissionNavSkeleton } from "../../components/skeleton";
import { SubmissionHeader } from "../../components/submission-header";
import { SubmissionNav } from "../../components/submission-nav";
import { useSubmission } from "../../hooks/use-submission";
import {
    dismissUploadStatus,
    getUploadStatusSnapshot,
    sectionsForDocumentTypes,
    subscribeUploadStatus,
} from "../../state/upload-status";
import type { SubmissionForm, SubmissionStatus } from "../../types/submission";
import { blankSubmissionForm } from "./blank-detail";
import { SubmissionContext } from "./context";
import { SUBMISSION_SECTIONS } from "./sections";

export interface HeaderPatch {
    status?: SubmissionStatus;
    assignee?: number | null;
    approver?: number | null;
}

/** Atlas-ui Select feeds: `id` (selectedKey) is the section's relative
 *  path so `navigate(id)` resolves correctly under both
 *  `/submissions/:id/<section>` and `/submissions/new`. */
const SECTION_ITEMS = SUBMISSION_SECTIONS.map((s) => ({ id: s.path, label: s.label }));

export function SubmissionLayout() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isCreate = !id;
    const query = useSubmission(isCreate ? undefined : id);

    /** Match the current section by the trailing path segment(s) so the
     *  mobile section Select shows the right active option. Falls back
     *  to the first section if nothing matches (e.g. on `/submissions/:id`
     *  pre-redirect). */
    const activeSection =
        SUBMISSION_SECTIONS.find((s) => location.pathname.endsWith(`/${s.path}`)) ?? SUBMISSION_SECTIONS[0];

    // In create mode we seed with a blank form right away. In edit mode
    // we wait for the GET to land before rendering the body.
    const initialDetail = useMemo(() => (isCreate ? blankSubmissionForm() : null), [isCreate]);
    const [localDetail, setLocalDetail] = useState<SubmissionForm | null>(initialDetail);

    const setDetail = useCallback((next: SubmissionForm) => {
        setLocalDetail(next);
    }, []);

    const detail = localDetail ?? (query.status === "ready" ? query.data : null);
    const mode: "create" | "edit" = detail && detail.id ? "edit" : "create";
    const loading = !isCreate && query.status === "loading" && !detail;
    const error = !isCreate && query.status === "error" && !detail ? query.error : null;

    // The backend sets `is_bor_received` to false when a duplicate
    // facility is detected (null = no duplicate, true = BOR resolved).
    // The Schedule warning banner + nav icon show only in the `false`
    // case. Lifted here so the submission nav can flag the item.
    const scheduleHasWarning = detail?.isBorReceived === false;

    // ── OR-1400: consume the panel's Update Submission press ──────────
    // The worker already enriched the submission server-side; the button
    // gates when the UI reflects it (Andressa's dev note — no page
    // refresh under the user). Refetch, toast, and flag the sections the
    // extracted document types feed.
    const uploadStatus = useSyncExternalStore(subscribeUploadStatus, getUploadStatusSnapshot, getUploadStatusSnapshot);
    const [autofillToast, setAutofillToast] = useState<null | "success" | "error">(null);
    const [updatedSections, setUpdatedSections] = useState<ReadonlySet<string>>(() => new Set());
    const detailId = detail?.id;
    useEffect(() => {
        if (uploadStatus.phase !== "applied") return;
        const { submissionId, documentTypes } = uploadStatus;
        // Wait for the submission to load before consuming the event: an
        // early dismiss here would swallow it and leave the user looking at
        // stale data with nothing to press.
        if (!detailId) return;
        dismissUploadStatus();
        if (submissionId !== detailId) return; // applied on another submission
        void getSubmission(submissionId)
            .then((wire) => {
                setLocalDetail(fromWireSubmission(wire));
                setUpdatedSections(sectionsForDocumentTypes(documentTypes));
                setAutofillToast("success");
            })
            .catch((err: unknown) => {
                console.error("Failed to refresh the submission after auto-fill", err);
                setAutofillToast("error");
            });
    }, [uploadStatus, detailId]);

    // Visiting a flagged section clears its marker — the user has seen it.
    useEffect(() => {
        setUpdatedSections((prev) => {
            if (!prev.has(activeSection.id)) return prev;
            const next = new Set(prev);
            next.delete(activeSection.id);
            return next;
        });
    }, [activeSection.id]);

    const handleHeaderPatch = useCallback(
        async (patch: HeaderPatch) => {
            if (!detail) return;
            // Until the submission has a real id there's nothing to PATCH;
            // the create POST will pick up the chosen values on save.
            if (!detail.id) {
                setLocalDetail({ ...detail, ...patch });
                return;
            }
            const previous = detail;
            setLocalDetail({ ...previous, ...patch });
            try {
                const wire = await patchSubmission(previous.id, {
                    status: patch.status,
                    assignee_id: patch.assignee,
                    approver_id: patch.approver,
                });
                setLocalDetail(fromWireSubmission(wire));
            } catch (err) {
                console.error("Failed to persist submission change", err);
                setLocalDetail(previous);
            }
        },
        [detail],
    );

    const markBorReceived = useCallback(async () => {
        if (!detail || !detail.id) return;
        const previous = detail;
        // Optimistic: clear the warning immediately, then reconcile with
        // the server's refreshed payload.
        setLocalDetail({ ...previous, isBorReceived: true });
        try {
            const wire = await patchSubmission(previous.id, { is_bor_received: true });
            setLocalDetail(fromWireSubmission(wire));
        } catch (err) {
            console.error("Failed to mark BOR as received", err);
            setLocalDetail(previous);
        }
    }, [detail]);

    const isLoadingHeader = loading;

    return (
        <div className="bg-secondary flex min-h-screen flex-col lg:h-screen lg:flex-row">
            <OrionSidebar />

            <div className="bg-primary flex flex-1 flex-col overflow-hidden">
                {/* Breadcrumb replaced by a back arrow next to the title in
                  * SubmissionHeader (Andressa jun-26) — it took up a whole row. */}
                {detail ? (
                    <SubmissionHeader
                        detail={detail}
                        onPatch={handleHeaderPatch}
                        // Mobile-only section navigator — pinned right below
                        // the title/subtitle block, above the Status / UW /
                        // Approval UW dropdown row, matching Figma
                        // 356-20885. Reuses the same atlas-ui Select
                        // component as those three header dropdowns so
                        // height (h-9 / 36px) and styling stay consistent.
                        // Hidden at lg+ — the desktop aside owns navigation
                        // there.
                        sectionNav={
                            <div className="flex flex-col gap-4 lg:hidden">
                                <Select
                                    size="sm"
                                    aria-label="Section"
                                    selectedKey={activeSection.path}
                                    items={SECTION_ITEMS}
                                    onSelectionChange={(key) => {
                                        if (key === null || key === activeSection.path) return;
                                        // Create mode (no id yet): every section
                                        // but UW Information needs a saved
                                        // submission, so block navigation away
                                        // until "Save & Continue" creates it.
                                        if (!detail.id && key !== "uw-information") return;
                                        // Relative navigation works from both
                                        // `/submissions/:id/<section>` and
                                        // `/submissions/new` (create mode).
                                        navigate(String(key));
                                    }}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                {/* Edge-to-edge separator (the `-mx-4` cancels
                                  * the header's `px-4` so the line spans the
                                  * full chrome width) followed by the active
                                  * section's label at 16px (per Figma
                                  * 356-20885). Reads as the section H2 above
                                  * the Status / UW / Approval UW dropdowns. */}
                                <div aria-hidden="true" className="border-secondary -mx-4 border-t" />
                                <h2 className="text-base font-semibold text-primary">{activeSection.label}</h2>
                            </div>
                        }
                    />
                ) : isLoadingHeader ? (
                    <SubmissionHeaderSkeleton />
                ) : null}

                <div className="bg-secondary flex flex-1 overflow-hidden">
                    <aside className="border-secondary bg-primary hidden border-r p-4 lg:block">
                        {detail ? (
                            <SubmissionNav
                                scheduleHasWarning={scheduleHasWarning}
                                createMode={!detail.id}
                                updatedSections={updatedSections}
                            />
                        ) : (
                            <SubmissionNavSkeleton />
                        )}
                    </aside>

                    {/* `relative` makes this scroll box the containing block for
                      * absolutely-positioned descendants (e.g. react-aria's
                      * VisuallyHidden checkbox inputs) — without it they escape
                      * the overflow clip and stretch the WINDOW scroll height
                      * to their static position. */}
                    {/* lg+: bounded flex (lg:min-h-0) so single-table pages can
                      * fill the height and scroll the table internally; mobile
                      * keeps the whole-section scroll. */}
                    <section className="atlas-scrollbar relative flex flex-1 flex-col gap-6 overflow-y-auto px-4 pt-4 sm:px-8 sm:pt-6 lg:min-h-0">
                        {/* Auto-fill outcome toast (OR-1400) — copy verbatim
                          * from the approved Figma. Floats ABOVE the section
                          * (498px, centred, shadow) rather than sitting in the
                          * flow, so nothing below it shifts when it appears. */}
                        {autofillToast && (
                            <div className="pointer-events-none absolute inset-x-0 top-4 z-30 flex justify-center px-4">
                                <FeaturedAlert
                                    className="pointer-events-auto w-[498px] max-w-full shadow-lg"
                                    color={autofillToast === "success" ? "success" : "error"}
                                    icon={autofillToast === "success" ? CheckCircle : AlertCircle}
                                    title={autofillToast === "success" ? "Auto-fill complete!" : "Auto-fill couldn't complete"}
                                    description={
                                        autofillToast === "success"
                                            ? "New information was added to highlighted sections."
                                            : "Something went wrong while extracting your data. Please try again."
                                    }
                                    onClose={() => setAutofillToast(null)}
                                    data-autotest="submission-autofill-toast"
                                />
                            </div>
                        )}
                        {loading && <SubmissionDetailSkeleton />}
                        {error && (
                            <p className="border-secondary bg-primary text-tertiary rounded-xl border p-6 text-sm">
                                Couldn't load submission <code>{id}</code>. {error.message}
                            </p>
                        )}
                        {detail && (
                            <SubmissionContext.Provider
                                value={{ detail, setDetail, mode, scheduleHasWarning, markBorReceived }}
                            >
                                <Outlet />
                            </SubmissionContext.Provider>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}
